import { computeArea } from "@/lib/tool-kit/geometry/area";
import {
  addExact,
  type Exact,
  exactPi,
  exactRational,
  exactSqrt,
  mulExact,
  scaleExact,
  toNumberExact,
} from "@/lib/tool-kit/geometry/exact";
import {
  DEFAULT_FORMAT,
  exactText,
  type ResultFormat,
  sub,
  text,
} from "@/lib/tool-kit/geometry/working";
import {
  addR,
  divR,
  intR,
  mulR,
  type Rational,
  signR,
  subR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * Volume and surface area of the common solids, kept exact.
 *
 * Everything is computed from rational measurements, so a cylinder of radius 3 and
 * height 5 has volume 45π and total surface area 48π, a cone of radius 3 and height 4
 * has slant height exactly 5, and a square pyramid's slant faces come out as radicals
 * in simplest form. The one exception is the general ellipsoid, whose surface area has
 * no closed form: it is Knud Thomsen's approximation, and is labelled as one.
 */

export const SOLID_IDS = [
  "cube",
  "box",
  "triPrism",
  "cylinder",
  "cone",
  "sphere",
  "hemisphere",
  "pyramid",
  "frustum",
  "ellipsoid",
] as const;

export type SolidId = (typeof SOLID_IDS)[number];

export type SolidFieldId =
  | "side"
  | "length"
  | "width"
  | "height"
  | "radius"
  | "sideA"
  | "sideB"
  | "sideC"
  | "prismLength"
  | "bottomRadius"
  | "topRadius"
  | "semiA"
  | "semiB"
  | "semiC";

export const SOLID_FIELDS: Record<SolidId, readonly SolidFieldId[]> = {
  cube: ["side"],
  box: ["length", "width", "height"],
  triPrism: ["sideA", "sideB", "sideC", "prismLength"],
  cylinder: ["radius", "height"],
  cone: ["radius", "height"],
  sphere: ["radius"],
  hemisphere: ["radius"],
  pyramid: ["length", "width", "height"],
  frustum: ["bottomRadius", "topRadius", "height"],
  ellipsoid: ["semiA", "semiB", "semiC"],
};

export type SolidMetricKey =
  | "baseArea"
  | "topArea"
  | "slantHeight"
  | "slantOnLength"
  | "slantOnWidth"
  | "spaceDiagonal"
  | "faceDiagonal"
  | "crossSection";

export interface SolidMetric {
  key: SolidMetricKey;
  exact: Exact;
  value: number;
  kind: "length" | "area";
}

/** The flat pattern a solid unfolds into, for the surface-area page. */
export type NetKind =
  | "box"
  | "triPrism"
  | "cylinder"
  | "cone"
  | "pyramid"
  | "frustum"
  | "none";

export type SolidError = "positive" | "triangle";

export interface SolidResult {
  ok: true;
  volume: Exact;
  volumeValue: number;
  /** Side surface only (no bases); null for solids with no separate sides. */
  lateral: Exact | null;
  lateralValue: number | null;
  /** Total surface area; null when only an approximation exists. */
  total: Exact | null;
  totalValue: number;
  /** True when the surface area is an approximation (the general ellipsoid). */
  approximate: boolean;
  metrics: SolidMetric[];
  volumeFormula: string;
  volumeSteps: string[];
  areaFormulas: string[];
  areaSteps: string[];
  net: NetKind;
}

export interface SolidFailure {
  ok: false;
  error: SolidError;
}

type Draft = Omit<SolidResult, "ok">;
type Fields = Record<string, Rational>;

const TWO = intR(2);
const THREE = intR(3);
const FOUR = intR(4);
const SIX = intR(6);
const THIRD = divR(intR(1), THREE);
const THOMSEN_P = 1.6075;

function square(value: Rational): Rational {
  return mulR(value, value);
}

function cube(value: Rational): Rational {
  return mulR(value, square(value));
}

function rational(value: Rational): Exact {
  return exactRational(value);
}

function lengthMetric(key: SolidMetricKey, exact: Exact): SolidMetric {
  return { key, exact, value: toNumberExact(exact), kind: "length" };
}

function areaMetric(key: SolidMetricKey, exact: Exact): SolidMetric {
  return { key, exact, value: toNumberExact(exact), kind: "area" };
}

function surfaceOf(
  lateral: Exact | null,
  total: Exact
): Pick<
  Draft,
  "lateral" | "lateralValue" | "total" | "totalValue" | "approximate"
> {
  return {
    lateral,
    lateralValue: lateral ? toNumberExact(lateral) : null,
    total,
    totalValue: toNumberExact(total),
    approximate: false,
  };
}

function cubeSolid(f: Fields, c: ResultFormat): Draft {
  const a = f.side;
  const volume = rational(cube(a));
  const total = rational(mulR(SIX, square(a)));
  const lateral = rational(mulR(FOUR, square(a)));
  return {
    volume,
    volumeValue: toNumberExact(volume),
    ...surfaceOf(lateral, total),
    metrics: [
      lengthMetric("faceDiagonal", mulExact(rational(a), exactSqrt(TWO))),
      lengthMetric("spaceDiagonal", mulExact(rational(a), exactSqrt(THREE))),
    ],
    volumeFormula: "V = a³",
    volumeSteps: [`V = a³ = ${sub(a, c)}³ = ${exactText(volume, c)}`],
    areaFormulas: ["LA = 4a²", "SA = 6a²"],
    areaSteps: [
      `LA = 4a² = 4 × ${sub(a, c)}² = ${exactText(lateral, c)}`,
      `SA = 6a² = 6 × ${sub(a, c)}² = ${exactText(total, c)}`,
    ],
    net: "box",
  };
}

function boxSolid(f: Fields, c: ResultFormat): Draft {
  const { length: l, width: w, height: h } = f;
  const volume = rational(mulR(mulR(l, w), h));
  const lateral = rational(mulR(mulR(TWO, h), addR(l, w)));
  const total = rational(
    mulR(TWO, addR(addR(mulR(l, w), mulR(l, h)), mulR(w, h)))
  );
  return {
    volume,
    volumeValue: toNumberExact(volume),
    ...surfaceOf(lateral, total),
    metrics: [
      lengthMetric(
        "spaceDiagonal",
        exactSqrt(addR(addR(square(l), square(w)), square(h)))
      ),
    ],
    volumeFormula: "V = l × w × h",
    volumeSteps: [
      `V = l × w × h = ${text(l, c)} × ${text(w, c)} × ${text(h, c)} = ${exactText(volume, c)}`,
    ],
    areaFormulas: ["LA = 2h(l + w)", "SA = 2(lw + lh + wh)"],
    areaSteps: [
      `LA = 2h(l + w) = 2 × ${text(h, c)} × (${text(l, c)} + ${text(w, c)}) = ${exactText(lateral, c)}`,
      `SA = 2(lw + lh + wh) = 2 × (${text(mulR(l, w), c)} + ${text(mulR(l, h), c)} + ${text(mulR(w, h), c)}) = ${exactText(total, c)}`,
    ],
    net: "box",
  };
}

function triPrismSolid(f: Fields, c: ResultFormat): Draft | SolidError {
  const length = f.prismLength;
  const base = computeArea(
    "triangle3",
    { lengths: { sideA: f.sideA, sideB: f.sideB, sideC: f.sideC } },
    c
  );
  if (!base.ok || base.area === null) {
    return "triangle";
  }
  const perimeter = addR(addR(f.sideA, f.sideB), f.sideC);
  const volume = mulExact(base.area, rational(length));
  const lateral = rational(mulR(perimeter, length));
  const total = addExact(scaleExact(base.area, TWO), lateral);
  const working = base.steps.map((step) => step.replace("A = ", "B = "));
  return {
    volume,
    volumeValue: toNumberExact(volume),
    ...surfaceOf(lateral, total),
    metrics: [areaMetric("baseArea", base.area)],
    volumeFormula: "V = B × L",
    volumeSteps: [
      ...working,
      `V = B × L = ${exactText(base.area, c)} × ${text(length, c)} = ${exactText(volume, c)}`,
    ],
    areaFormulas: ["LA = (a + b + c) × L", "SA = LA + 2B"],
    areaSteps: [
      ...working,
      `LA = (a + b + c) × L = ${text(perimeter, c)} × ${text(length, c)} = ${exactText(lateral, c)}`,
      `SA = LA + 2B = ${exactText(lateral, c)} + 2 × ${exactText(base.area, c)} = ${exactText(total, c)}`,
    ],
    net: "triPrism",
  };
}

function cylinderSolid(f: Fields, c: ResultFormat): Draft {
  const { radius: r, height: h } = f;
  const base = exactPi(square(r));
  const volume = exactPi(mulR(square(r), h));
  const lateral = exactPi(mulR(mulR(TWO, r), h));
  const total = addExact(lateral, scaleExact(base, TWO));
  return {
    volume,
    volumeValue: toNumberExact(volume),
    ...surfaceOf(lateral, total),
    metrics: [areaMetric("baseArea", base)],
    volumeFormula: "V = π × r² × h",
    volumeSteps: [
      `V = π × r² × h = π × ${sub(r, c)}² × ${text(h, c)} = ${exactText(volume, c)}`,
    ],
    areaFormulas: ["B = π × r²", "LA = 2 × π × r × h", "SA = LA + 2B"],
    areaSteps: [
      `B = π × r² = π × ${sub(r, c)}² = ${exactText(base, c)}`,
      `LA = 2 × π × r × h = 2 × π × ${sub(r, c)} × ${text(h, c)} = ${exactText(lateral, c)}`,
      `SA = LA + 2B = ${exactText(lateral, c)} + 2 × ${exactText(base, c)} = ${exactText(total, c)}`,
    ],
    net: "cylinder",
  };
}

function coneSolid(f: Fields, c: ResultFormat): Draft {
  const { radius: r, height: h } = f;
  const base = exactPi(square(r));
  const volume = exactPi(mulR(THIRD, mulR(square(r), h)));
  const slant = exactSqrt(addR(square(r), square(h)));
  const lateral = mulExact(exactPi(r), slant);
  const total = addExact(lateral, base);
  return {
    volume,
    volumeValue: toNumberExact(volume),
    ...surfaceOf(lateral, total),
    metrics: [lengthMetric("slantHeight", slant), areaMetric("baseArea", base)],
    volumeFormula: "V = (1/3) × π × r² × h",
    volumeSteps: [
      `V = (1/3) × π × r² × h = (1/3) × π × ${sub(r, c)}² × ${text(h, c)} = ${exactText(volume, c)}`,
    ],
    areaFormulas: [
      "l = √(r² + h²)",
      "B = π × r²",
      "LA = π × r × l",
      "SA = LA + B",
    ],
    areaSteps: [
      `l = √(r² + h²) = √(${sub(r, c)}² + ${sub(h, c)}²) = ${exactText(slant, c)}`,
      `B = π × r² = π × ${sub(r, c)}² = ${exactText(base, c)}`,
      `LA = π × r × l = π × ${sub(r, c)} × ${exactText(slant, c)} = ${exactText(lateral, c)}`,
      `SA = LA + B = ${exactText(lateral, c)} + ${exactText(base, c)} = ${exactText(total, c)}`,
    ],
    net: "cone",
  };
}

function sphereSolid(f: Fields, c: ResultFormat): Draft {
  const r = f.radius;
  const volume = exactPi(mulR(divR(FOUR, THREE), cube(r)));
  const total = exactPi(mulR(FOUR, square(r)));
  return {
    volume,
    volumeValue: toNumberExact(volume),
    ...surfaceOf(null, total),
    metrics: [areaMetric("crossSection", exactPi(square(r)))],
    volumeFormula: "V = (4/3) × π × r³",
    volumeSteps: [
      `V = (4/3) × π × r³ = (4/3) × π × ${sub(r, c)}³ = ${exactText(volume, c)}`,
    ],
    areaFormulas: ["SA = 4 × π × r²"],
    areaSteps: [
      `SA = 4 × π × r² = 4 × π × ${sub(r, c)}² = ${exactText(total, c)}`,
    ],
    net: "none",
  };
}

function hemisphereSolid(f: Fields, c: ResultFormat): Draft {
  const r = f.radius;
  const volume = exactPi(mulR(divR(TWO, THREE), cube(r)));
  const curved = exactPi(mulR(TWO, square(r)));
  const base = exactPi(square(r));
  const total = addExact(curved, base);
  return {
    volume,
    volumeValue: toNumberExact(volume),
    ...surfaceOf(curved, total),
    metrics: [areaMetric("baseArea", base)],
    volumeFormula: "V = (2/3) × π × r³",
    volumeSteps: [
      `V = (2/3) × π × r³ = (2/3) × π × ${sub(r, c)}³ = ${exactText(volume, c)}`,
    ],
    areaFormulas: ["CSA = 2 × π × r²", "SA = CSA + π × r²"],
    areaSteps: [
      `CSA = 2 × π × r² = 2 × π × ${sub(r, c)}² = ${exactText(curved, c)}`,
      `SA = CSA + π × r² = ${exactText(curved, c)} + ${exactText(base, c)} = ${exactText(total, c)}`,
    ],
    net: "none",
  };
}

function pyramidSolid(f: Fields, c: ResultFormat): Draft {
  const { length: l, width: w, height: h } = f;
  const halfL = divR(l, TWO);
  const halfW = divR(w, TWO);
  const base = rational(mulR(l, w));
  const volume = rational(mulR(THIRD, mulR(mulR(l, w), h)));
  const onLength = exactSqrt(addR(square(h), square(halfW)));
  const onWidth = exactSqrt(addR(square(h), square(halfL)));
  const lateral = addExact(
    mulExact(rational(l), onLength),
    mulExact(rational(w), onWidth)
  );
  const total = addExact(lateral, base);
  return {
    volume,
    volumeValue: toNumberExact(volume),
    ...surfaceOf(lateral, total),
    metrics: [
      lengthMetric("slantOnLength", onLength),
      lengthMetric("slantOnWidth", onWidth),
      areaMetric("baseArea", base),
    ],
    volumeFormula: "V = (1/3) × l × w × h",
    volumeSteps: [
      `V = (1/3) × l × w × h = (1/3) × ${text(l, c)} × ${text(w, c)} × ${text(h, c)} = ${exactText(volume, c)}`,
    ],
    areaFormulas: [
      "s₁ = √(h² + (w/2)²),  s₂ = √(h² + (l/2)²)",
      "LA = l × s₁ + w × s₂",
      "SA = LA + l × w",
    ],
    areaSteps: [
      `s₁ = √(h² + (w/2)²) = √(${sub(h, c)}² + ${sub(halfW, c)}²) = ${exactText(onLength, c)}`,
      `s₂ = √(h² + (l/2)²) = √(${sub(h, c)}² + ${sub(halfL, c)}²) = ${exactText(onWidth, c)}`,
      `LA = l × s₁ + w × s₂ = ${text(l, c)} × ${exactText(onLength, c)} + ${text(w, c)} × ${exactText(onWidth, c)} = ${exactText(lateral, c)}`,
      `SA = LA + l × w = ${exactText(lateral, c)} + ${exactText(base, c)} = ${exactText(total, c)}`,
    ],
    net: "pyramid",
  };
}

function frustumSolid(f: Fields, c: ResultFormat): Draft {
  const { bottomRadius: big, topRadius: small, height: h } = f;
  const volume = exactPi(
    mulR(
      divR(h, THREE),
      addR(addR(square(big), mulR(big, small)), square(small))
    )
  );
  const slant = exactSqrt(addR(square(subR(big, small)), square(h)));
  const lateral = mulExact(exactPi(addR(big, small)), slant);
  const bases = exactPi(addR(square(big), square(small)));
  const total = addExact(lateral, bases);
  return {
    volume,
    volumeValue: toNumberExact(volume),
    ...surfaceOf(lateral, total),
    metrics: [
      lengthMetric("slantHeight", slant),
      areaMetric("baseArea", exactPi(square(big))),
      areaMetric("topArea", exactPi(square(small))),
    ],
    volumeFormula: "V = (1/3) × π × h × (R² + R × r + r²)",
    volumeSteps: [
      `V = (1/3) × π × h × (R² + R × r + r²) = (1/3) × π × ${sub(h, c)} × (${sub(big, c)}² + ${sub(big, c)} × ${sub(small, c)} + ${sub(small, c)}²) = ${exactText(volume, c)}`,
    ],
    areaFormulas: [
      "l = √((R − r)² + h²)",
      "LA = π × (R + r) × l",
      "SA = LA + π × R² + π × r²",
    ],
    areaSteps: [
      `l = √((R − r)² + h²) = √((${text(big, c)} − ${text(small, c)})² + ${sub(h, c)}²) = ${exactText(slant, c)}`,
      `LA = π × (R + r) × l = π × (${text(big, c)} + ${text(small, c)}) × ${exactText(slant, c)} = ${exactText(lateral, c)}`,
      `SA = LA + π × R² + π × r² = ${exactText(lateral, c)} + ${exactText(bases, c)} = ${exactText(total, c)}`,
    ],
    net: "frustum",
  };
}

/** Knud Thomsen's approximation, p = 1.6075: within about 1.06% of the true value. */
export function ellipsoidSurfaceApprox(
  a: number,
  b: number,
  c: number
): number {
  const p = THOMSEN_P;
  const mean = ((a * b) ** p + (a * c) ** p + (b * c) ** p) / 3;
  return 4 * Math.PI * mean ** (1 / p);
}

function ellipsoidSolid(f: Fields, c: ResultFormat): Draft {
  const { semiA: a, semiB: b, semiC: d } = f;
  const volume = exactPi(mulR(divR(FOUR, THREE), mulR(mulR(a, b), d)));
  const total = ellipsoidSurfaceApprox(
    toNumberR(a),
    toNumberR(b),
    toNumberR(d)
  );
  return {
    volume,
    volumeValue: toNumberExact(volume),
    lateral: null,
    lateralValue: null,
    total: null,
    totalValue: total,
    approximate: true,
    metrics: [],
    volumeFormula: "V = (4/3) × π × a × b × c",
    volumeSteps: [
      `V = (4/3) × π × a × b × c = (4/3) × π × ${text(a, c)} × ${text(b, c)} × ${text(d, c)} = ${exactText(volume, c)}`,
    ],
    areaFormulas: [
      "SA ≈ 4π × ((a^p b^p + a^p c^p + b^p c^p) / 3)^(1/p),  p = 1.6075",
    ],
    areaSteps: [],
    net: "none",
  };
}

function allPositive(values: readonly (Rational | undefined)[]): boolean {
  return values.every((value) => value !== undefined && signR(value) > 0);
}

function build(
  solid: SolidId,
  f: Fields,
  format: ResultFormat
): Draft | SolidError {
  switch (solid) {
    case "cube":
      return cubeSolid(f, format);
    case "box":
      return boxSolid(f, format);
    case "triPrism":
      return triPrismSolid(f, format);
    case "cylinder":
      return cylinderSolid(f, format);
    case "cone":
      return coneSolid(f, format);
    case "sphere":
      return sphereSolid(f, format);
    case "hemisphere":
      return hemisphereSolid(f, format);
    case "pyramid":
      return pyramidSolid(f, format);
    case "frustum":
      return frustumSolid(f, format);
    case "ellipsoid":
      return ellipsoidSolid(f, format);
    default:
      return "positive";
  }
}

/** Volume, surface areas and the working for a solid whose measurements are all in one unit. */
export function computeSolid(
  solid: SolidId,
  lengths: Partial<Record<SolidFieldId, Rational>>,
  format: ResultFormat = DEFAULT_FORMAT
): SolidResult | SolidFailure {
  const ids = SOLID_FIELDS[solid];
  if (!allPositive(ids.map((id) => lengths[id]))) {
    return { ok: false, error: "positive" };
  }
  const outcome = build(solid, lengths as Fields, format);
  if (typeof outcome === "string") {
    return { ok: false, error: outcome };
  }
  return { ok: true, ...outcome };
}
